// 가공 진행 화면(공구 경로) - 장비 상세 "가공 진행" 탭 + 모니터링 보드 썸네일에서 쓰는
// 순수 로직/포맷만 모아 둔다. 검증은 eqpToolpathFormat.test.js.
// 계획: 설비모니터링/가공 진행 화면(공구 경로) 계획.md

// ============================= 화면 상태(단계) =============================
// progress 응답의 fileFound/indexed/line 을 보고 어떤 안내를 보여줄지 정한다(우선순위 순서 - 계획 5번).
export const TOOLPATH_STATE = {
  LOADING: 'LOADING',
  FILE_NOT_FOUND: 'FILE_NOT_FOUND',
  INDEXING: 'INDEXING',
  LOCATING: 'LOCATING',
  OK: 'OK',
};

export const getToolpathStateKind = (progress) => {
  if (!progress) return TOOLPATH_STATE.LOADING;
  if (progress.fileFound === false) return TOOLPATH_STATE.FILE_NOT_FOUND;
  if (progress.indexed === false) return TOOLPATH_STATE.INDEXING;
  if (progress.line === null || progress.line === undefined) return TOOLPATH_STATE.LOCATING;
  return TOOLPATH_STATE.OK;
};

// 상태별 안내 문구 - FILE_NOT_FOUND는 프로그램 번호가 필요해서 함수로 둔다.
export const formatFileNotFoundMessage = (prgNo) =>
  `실행 중 프로그램(${prgNo || '?'}) 파일이 아직 백업되지 않았어요 — FTP 백업을 기다리는 중`;

export const INDEXING_MESSAGE = '경로를 분석하는 중이에요(큰 파일은 몇 분 걸려요)';
export const LOCATING_MESSAGE = '현재 위치를 찾는 중이에요';

// ============================= 숫자/라벨 포맷 =============================
// pctLine/pctLength 는 cad-service가 이미 0~100 스케일(%)로 내려준다(ncpath.py) - clamp만 한다.
export const clampPctValue = (pct) => {
  const n = Number(pct);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
};

export const formatPctValue = (pct) => {
  const n = Number(pct);
  if (pct === null || pct === undefined || !Number.isFinite(n)) return '-';
  return `${Math.round(n)}%`;
};

// remainingLengthMm(mm) -> "N.Nm"
export const formatRemainingMeters = (mm) => {
  const n = Number(mm);
  if (mm === null || mm === undefined || !Number.isFinite(n)) return '-';
  return `${(n / 1000).toFixed(1)}m`;
};

export const formatProgramLabel = (prgNo, fileNm) => {
  if (!prgNo && !fileNm) return '-';
  // 파일명이 프로그램 번호와 같으면(O7000 / O7000) 한 번만 쓴다 — 확장자(.NC)와 대소문자는 무시.
  const bare = (v) => String(v).toUpperCase().replace(/\.NC$/, '');
  if (prgNo && fileNm) return bare(prgNo) === bare(fileNm) ? prgNo : `${prgNo} / ${fileNm}`;
  return prgNo || fileNm;
};

export const formatFeed = (feed) => {
  const n = Number(feed);
  if (feed === null || feed === undefined || !Number.isFinite(n)) return '-';
  return `${n.toLocaleString()} mm/min`;
};

export const formatSpindleRpm = (rpm) => {
  const n = Number(rpm);
  if (rpm === null || rpm === undefined || !Number.isFinite(n)) return '-';
  return `${n.toLocaleString()} rpm`;
};

// ============================= 남은 시간 추정(ETA) =============================
// 최근 10분 창의 (시각, remainingLengthMm) 샘플로 속도를 구해 추정한다(계획: "최근 10분 속도").
// 운전 중(RUNNING)일 때만 호출 쪽에서 샘플을 쌓게 한다 - 정지/알람 중에는 변화가 없어 속도가 0으로 왜곡됨.
export const ETA_WINDOW_MS = 10 * 60 * 1000; // 10분
export const ETA_MIN_SPAN_MS = 60 * 1000; // 60초 이상 벌어져야 추정(계획: "2개 샘플·60초 이상")

// sample: { t: number(ms), remainingMm: number } - 유효하지 않으면 그대로 돌려준다(추가 안 함).
export const pushEtaSample = (samples, sample) => {
  const prev = Array.isArray(samples) ? samples : [];
  if (!sample || !Number.isFinite(sample.t) || !Number.isFinite(Number(sample.remainingMm))) {
    return prev;
  }
  const next = [...prev, { t: sample.t, remainingMm: Number(sample.remainingMm) }];
  const cutoff = sample.t - ETA_WINDOW_MS;
  return next.filter((s) => s.t >= cutoff);
};

// 남은 시간(초) 추정 - 샘플이 2개 미만이거나 60초 이상 안 벌어졌거나, 길이가 안 줄었으면(정지/노이즈) null.
export const computeEtaSec = (samples) => {
  if (!Array.isArray(samples) || samples.length < 2) return null;
  const first = samples[0];
  const last = samples[samples.length - 1];
  const spanMs = last.t - first.t;
  if (spanMs < ETA_MIN_SPAN_MS) return null;
  const deltaMm = first.remainingMm - last.remainingMm;
  if (!Number.isFinite(deltaMm) || deltaMm <= 0) return null;
  const rateMmPerSec = deltaMm / (spanMs / 1000);
  if (!Number.isFinite(rateMmPerSec) || rateMmPerSec <= 0) return null;
  return last.remainingMm / rateMmPerSec;
};

// "약 N시간 M분" / "약 M분" / "거의 완료" / "계산 중"
export const formatEtaText = (etaSec) => {
  if (etaSec === null || etaSec === undefined || !Number.isFinite(Number(etaSec))) return '계산 중';
  const sec = Number(etaSec);
  if (sec <= 0) return '거의 완료';
  const totalMin = Math.round(sec / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h <= 0) return `약 ${m}분`;
  return `약 ${h}시간 ${m}분`;
};
